import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Image, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { createEvent, updateEvent, getEvent, getCategories } from '@/lib/api';
import { toast } from 'sonner';

const DEFAULT_IMAGES = [
  'https://images.unsplash.com/photo-1701643561845-5802af626884?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1565293377763-c3cb5ae38bf9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1576941230616-1df1071ff6c0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1475552013742-eb6eb701988c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
];

export default function EventFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Revival Night',
    start_at: '',
    location: '',
    hero_image_url: DEFAULT_IMAGES[0],
    price: '',
    currency: 'usd',
    capacity: '',
    status: 'published',
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getCategories();
        setCategories(res.data.categories || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (isEdit) {
      const fetchEvent = async () => {
        try {
          const res = await getEvent(id);
          const event = res.data.event;
          setFormData({
            title: event.title || '',
            description: event.description || '',
            category: event.category || 'Revival Night',
            start_at: event.start_at ? event.start_at.slice(0, 16) : '',
            location: event.location || '',
            hero_image_url: event.hero_image_url || DEFAULT_IMAGES[0],
            price: event.price?.toString() || '',
            currency: event.currency || 'usd',
            capacity: event.capacity?.toString() || '',
            status: event.status || 'published',
          });
        } catch (e) {
          toast.error('Failed to load event');
          navigate('/admin');
        }
      };
      fetchEvent();
    }
  }, [id, isEdit, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.start_at || !formData.price || !formData.capacity) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        capacity: parseInt(formData.capacity),
        start_at: new Date(formData.start_at).toISOString(),
      };

      if (isEdit) {
        await updateEvent(id, payload);
        toast.success('Event updated successfully');
      } else {
        await createEvent(payload);
        toast.success('Event created successfully');
      }
      navigate('/admin');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--pm-surface)]">
      <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="rounded-lg"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="font-heading text-2xl font-semibold text-[color:var(--pm-ink)]">
            {isEdit ? 'Edit Event' : 'Create New Event'}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-lg">Event Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder="Revival Night: The Awakening"
                      className="mt-1 h-11 rounded-lg"
                      data-testid="admin-event-title-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Describe your event..."
                      rows={5}
                      className="mt-1 rounded-lg"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Category *</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(v) => handleChange('category', v)}
                      >
                        <SelectTrigger className="mt-1 h-11 rounded-lg" data-testid="admin-event-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="start_at">Date & Time *</Label>
                      <Input
                        id="start_at"
                        type="datetime-local"
                        value={formData.start_at}
                        onChange={(e) => handleChange('start_at', e.target.value)}
                        className="mt-1 h-11 rounded-lg"
                        data-testid="admin-event-date-picker"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      placeholder="Grace Cathedral, San Francisco, CA"
                      className="mt-1 h-11 rounded-lg"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-lg">Event Image</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="hero_image_url">Image URL</Label>
                    <Input
                      id="hero_image_url"
                      value={formData.hero_image_url}
                      onChange={(e) => handleChange('hero_image_url', e.target.value)}
                      placeholder="https://..."
                      className="mt-1 h-11 rounded-lg"
                    />
                  </div>
                  {formData.hero_image_url && (
                    <div className="rounded-xl overflow-hidden border border-[color:rgba(11,18,32,0.10)]">
                      <img
                        src={formData.hero_image_url}
                        alt="Preview"
                        className="w-full h-48 object-cover"
                        onError={(e) => { e.target.src = DEFAULT_IMAGES[0]; }}
                      />
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Or choose a preset image:</p>
                    <div className="grid grid-cols-4 gap-2">
                      {DEFAULT_IMAGES.map((url, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => handleChange('hero_image_url', url)}
                          className={`rounded-lg overflow-hidden border-2 transition-all ${
                            formData.hero_image_url === url
                              ? 'border-[color:var(--pm-gold)]'
                              : 'border-transparent hover:border-[color:rgba(11,18,32,0.20)]'
                          }`}
                        >
                          <img src={url} alt={`Preset ${i+1}`} className="w-full h-16 object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-lg">Pricing & Capacity</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="price">Ticket Price (USD) *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0.50"
                      value={formData.price}
                      onChange={(e) => handleChange('price', e.target.value)}
                      placeholder="25.00"
                      className="mt-1 h-11 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="capacity">Capacity *</Label>
                    <Input
                      id="capacity"
                      type="number"
                      min="1"
                      value={formData.capacity}
                      onChange={(e) => handleChange('capacity', e.target.value)}
                      placeholder="500"
                      className="mt-1 h-11 rounded-lg"
                      required
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
                <CardHeader className="pb-4">
                  <CardTitle className="font-heading text-lg">Publish</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => handleChange('status', v)}
                    >
                      <SelectTrigger className="mt-1 h-11 rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold shadow-[0_8px_18px_rgba(15,27,45,0.14)] hover:bg-[color:var(--pm-navy)] transition-all"
                    data-testid="admin-event-save-button"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : isEdit ? 'Update Event' : 'Create Event'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
